/**
 * Salesperson Analytics Page
 *
 * Detailed analytics for individual salesperson with commission data
 * and customer portfolio breakdown.
 */

import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSalespersonDetails, useCurrentPeriod } from "../hooks/useSalesData";
import { PeriodSelector } from "../components/sales/PeriodSelector";
import { CategoryChart } from "../components/sales/CategoryChart";
import { CategoryGrowthChart } from "../components/sales/CategoryGrowthChart";
import { CategoryRevenueChart } from "../components/sales/CategoryRevenueChart";
import { TrendChart } from "../components/sales/TrendChart";
import { TopCustomersChart } from "../components/sales/TopCustomersChart";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { DollarSign, FileText, TrendingUp, Users } from "lucide-react";
import { t } from "@/shared/lib/th-translations";

export default function SalespersonAnalytics() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentPeriod = useCurrentPeriod();
  const [selectedPeriod, setSelectedPeriod] = useState(currentPeriod);

  const { data, isLoading } = useSalespersonDetails(id, {
    period: selectedPeriod
  });

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="container mx-auto p-6">{t("salespersonAnalytics.notFound")}</div>;
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">{data.salespersonName}</h1>
          <p className="text-muted-foreground">
            {t("salespersonAnalytics.subtitle")}
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => navigate(`/analytics/salespersons/${id}/customers`)}
            className="gap-2"
          >
            <Users className="h-4 w-4" />
            View Customer Portfolio
          </Button>
          <PeriodSelector value={selectedPeriod} onChange={setSelectedPeriod} />
        </div>
      </div>

      {/* Summary Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Sales</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ฿{data.commissionData.totalSales.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              {data.analytics.periodComparison.revenueChangePercentage > 0 ? '+' : ''}
              {data.analytics.periodComparison.revenueChangePercentage.toFixed(1)}% from last period
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Projects</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {data.commissionData.invoiceCount}
            </div>
            <p className="text-xs text-muted-foreground">
              {data.analytics.periodComparison.projectChangeCount > 0 ? '+' : ''}
              {data.analytics.periodComparison.projectChangeCount} from last period
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Project Value</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ฿{Math.round(data.analytics.averageProjectValue).toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              {data.analytics.activeCustomerCount} active customers
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Period Comparison Card */}
      <TrendChart
        comparison={data.analytics.periodComparison}
        title="Period-over-Period Performance"
        description="Revenue and project comparison"
      />

      {/* Revenue by Category - Absolute Values */}
      <CategoryRevenueChart
        data={data.analytics.categoryDistribution}
        title="Revenue by Category"
        description="Total revenue for each product category"
      />

      {/* Category Growth Charts */}
      {data.analytics.categoryDistribution.some((cat: any) => cat.growth) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CategoryGrowthChart
            data={data.analytics.categoryDistribution}
            chartType="bar"
            metricType="revenue"
            title="Revenue Growth by Category"
            description="Period-over-period revenue change"
          />
          <CategoryGrowthChart
            data={data.analytics.categoryDistribution}
            chartType="bar"
            metricType="projectCount"
            title="Project Count Growth by Category"
            description="Period-over-period project count change"
          />
        </div>
      )}

      {/* Category Distribution & Top Customers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CategoryChart
          data={data.analytics.categoryDistribution}
          title="Sales Distribution by Category"
          description="Revenue breakdown across product categories"
        />
        <TopCustomersChart
          customers={data.analytics.topCustomers}
          title="Top Customers"
          description="Highest revenue customers this period"
          limit={10}
        />
      </div>
    </div>
  );
}
